import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { useEffect } from 'react';
import { ServiceCode } from '@common/enums';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import type { ServiceConsumerProp } from '../../utils/v1/loadServiceConsumer';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { CheckboxInput } from '../../../../fields/Checkbox';
import { NumberInput } from '../../../../fields/Number';
import { TextAreaInput } from '../../../../fields/TextArea';
import { GgzoEmblV1FollowingSteps } from '../../GGZOembl/v1/GgzoEmblV1FollowingSteps';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { useLoadServiceConsumer } from '../../utils/v1/loadServiceConsumer';
import { GgzoIntV1FollowingSteps } from './GgzoIntV1FollowingSteps';

interface IGgzoAddEditFormProps {
    buttonDisabled: boolean;
    emailKnown: boolean;
    saveRequest: (saveType: 'draft' | 'final') => Promise<void>;
}

export function GgzoAddEditForm(props: ICustomControlRequestAddEditProps & IGgzoAddEditFormProps) {
    const serviceConsumer = useLoadServiceConsumer(props.service.id, { employmentId: props.employmentId });

    return (
        <InnerGgzoAddEditForm
            {...props}
            serviceConsumer={serviceConsumer}
        />
    );
}

function InnerGgzoAddEditForm(props: ICustomControlRequestAddEditProps & IGgzoAddEditFormProps & ServiceConsumerProp) {
    useEffect(() => {
        const isNewRequest = !props.request.id;
        if (isNewRequest) {
            const heeftNameting = props.serviceConsumer?.followUpMeasurementGgz;
            const nameting = props.serviceConsumer?.weeksForFollowUpMeasurementGgz;
            const nametingOp =
                nameting && nameting <= 999 ? DateTime.now().add(nameting, 'weeks').format('YYYY-MM-DD') : null;
            props.handleChangeCustomControl({ heeftNameting, nameting, nametingOp });
        }
    }, []);

    return (
        <Form onSubmit={async () => null}>
            <CareProviderSelector
                {...props}
                entity={props.request}
            />

            {props.emailKnown && (
                <Alert appearance="warning">Controleer of het e-mailadres van de patiënt juist is.</Alert>
            )}

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.emailadres')}>
                <FormLabel isRequired>E-mailadres</FormLabel>
                <InputContainer>
                    <TextAreaInput
                        automation="email"
                        error={props.errors.get('customControlData.emailadres')}
                        onChange={async (value) => await props.handleChangeCustomControl({ emailadres: value })}
                        onDoValidate={async () => await props.validateOneCustomControl('emailadres')}
                        placeholder="E-mailadres voor het versturen van de vragenlijst..."
                        value={props.request.customControlData.emailadres}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <GraderSelector
                {...props}
                careProviderAndRequesterAndSelectedProvider={{
                    ...props.request,
                    requesterOrganizationId: props.organizationId,
                }}
                isExaminationRequest
                procedurePerformerOrganizationId={props.organizationId}
            />

            <HorizontalFormGroup
                hasErrors={
                    props.errors.has('customControlData.nametingOp') ||
                    props.errors.has('customControlData.nameting') ||
                    props.errors.has('customControlData.heeftNameting')
                }
            >
                <FormLabel>Nameting sturen</FormLabel>
                <InputContainer size="auto">
                    <CheckboxInput
                        automation="nametingSturen"
                        checked={props.request.customControlData.heeftNameting}
                        error={props.errors.get('customControlData.heeftNameting')}
                        onChange={async (value) => {
                            await props.handleChangeCustomControl({ heeftNameting: value });
                            if (!value) {
                                await props.handleChangeCustomControl({ nameting: null, nametingOp: null });
                            }
                        }}
                        onDoValidate={async () => {
                            await props.validateOneCustomControl('heeftNameting');
                            if (props.errors.has('customControlData.nameting')) {
                                await props.validateOneCustomControl('nameting');
                            }
                        }}
                        text="Ja, na"
                    />
                </InputContainer>
                <InputContainer className="col-sm-12 col-xl-6">
                    <NumberInput
                        disabled={!props.request.customControlData.heeftNameting}
                        error={props.errors.get('customControlData.nameting')}
                        onChange={async (value) => {
                            if (!value || value <= 999) {
                                const nametingOp = value
                                    ? DateTime.now().add(value, 'weeks').format('YYYY-MM-DD')
                                    : null;
                                await props.handleChangeCustomControl({ nameting: value, nametingOp });
                            }
                        }}
                        onDoValidate={async () => {
                            await props.validateOneCustomControl('nameting');
                            if (props.errors.has('customControlData.heeftNameting')) {
                                await props.validateOneCustomControl('heeftNameting');
                            }
                        }}
                        text={`${props.request.customControlData.nameting === 1 ? 'week' : 'weken'} ${
                            props.request.customControlData.nameting
                                ? `(${DateTime.now()
                                      .add(props.request.customControlData.nameting, 'weeks')
                                      .format('D MMMM YYYY')})`
                                : ''
                        }`}
                        value={props.request.customControlData.nameting}
                        width="small"
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {props.service.code === ServiceCode.GGZOembl ? <GgzoEmblV1FollowingSteps /> : <GgzoIntV1FollowingSteps />}

            <ButtonContainer
                alignRight
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.request}
                        isExisting={!!props.request.id}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                    />,
                    <SaveButton
                        buttonDisabled={props.buttonDisabled}
                        save={async () => {
                            await props.saveRequest('draft');
                        }}
                    />,
                    <SaveSendButton
                        buttonDisabled={props.buttonDisabled}
                        saveSend={async () => {
                            await props.saveRequest('final');
                        }}
                    />,
                ]}
            />
        </Form>
    );
}
